import React from 'react';
import { useNavigate } from 'react-router-dom';
import './TermsOfService.css';

const TermsOfService = () => {
    const navigate = useNavigate();

    return (
        <div className="terms-container">
            <div className="terms-header">
                <button onClick={() => navigate('/')} className="back-btn">← Back to Home</button>
                <h1>Terms of Service</h1>
                <p className="last-updated">Last Updated: March 11, 2026</p>
            </div>

            <div className="terms-content">
                <section className="terms-section">
                    <h2>1. Acceptance of Terms</h2>
                    <p>
                        By accessing or using Launchway (web app and CLI), you agree to these Terms. If you do not agree, do not use the service.
                    </p>
                    <p>
                        These Terms may be updated over time. Continued use after updates means you accept the revised Terms.
                    </p>
                </section>

                <section className="terms-section">
                    <h2>2. Description of Service</h2>
                    <p>Launchway provides tools for job search workflow automation, including:</p>
                    <ul>
                        <li>Account and profile management</li>
                        <li>Resume tailoring and application assistance features</li>
                        <li>Job search and automated/assisted apply flows</li>
                        <li>Credits and usage-limited feature access</li>
                        <li>Optional third-party integrations</li>
                    </ul>
                    <p>
                        Some features use AI providers and third-party platforms to function. Feature behavior can change as the product evolves.
                    </p>
                </section>

                <section className="terms-section">
                    <h2>3. User Accounts and Eligibility</h2>

                    <h3>3.1 Eligibility</h3>
                    <p>You must be at least 18 years old to use our service. By creating an account, you represent and warrant that:</p>
                    <ul>
                        <li>You are at least 18 years of age</li>
                        <li>You have the legal capacity to enter into these Terms</li>
                        <li>All information you provide is accurate and truthful</li>
                        <li>You will maintain the accuracy of your account information</li>
                    </ul>

                    <h3>3.2 Account Security</h3>
                    <p>You are responsible for:</p>
                    <ul>
                        <li>Maintaining the confidentiality of your password</li>
                        <li>All activities that occur under your account</li>
                        <li>Notifying us immediately of any unauthorized access</li>
                        <li>Ensuring your account information is accurate and up to date</li>
                    </ul>

                    <h3>3.3 Account Termination</h3>
                    <p>We reserve the right to suspend or terminate your account at any time for:</p>
                    <ul>
                        <li>Violation of these Terms</li>
                        <li>Fraudulent or illegal activity</li>
                        <li>Abuse of the platform or other users</li>
                        <li>Failure to comply with applicable laws</li>
                    </ul>
                </section>

                <section className="terms-section">
                    <h2>4. Beta Access and Service Availability</h2>

                    <h3>4.1 Beta Program</h3>
                    <p>
                        Some features may be in beta, may require approval, and may be modified or removed at any time.
                    </p>

                    <h3>4.2 Service Availability</h3>
                    <p>
                        We aim for reliable availability but do not guarantee uninterrupted service. Any part of the service may be suspended, modified, or discontinued.
                    </p>
                </section>

                <section className="terms-section">
                    <h2>5. Acceptable Use Policy</h2>

                    <h3>5.1 Permitted Use</h3>
                    <p>You agree to use our service only for lawful purposes and in accordance with these Terms. You may use the service to:</p>
                    <ul>
                        <li>Apply for legitimate job opportunities</li>
                        <li>Manage your professional profile and application materials</li>
                        <li>Track your job application activities</li>
                    </ul>

                    <h3>5.2 Prohibited Activities</h3>
                    <p>You agree NOT to:</p>
                    <ul>
                        <li>Violate any applicable laws or regulations</li>
                        <li>Submit false, misleading, or fraudulent information</li>
                        <li>Impersonate any person or entity</li>
                        <li>Abuse, harass, or harm other users or third parties</li>
                        <li>Attempt to gain unauthorized access to our systems</li>
                        <li>Reverse engineer, decompile, or disassemble any part of the service</li>
                        <li>Use automated scripts or bots beyond the intended functionality</li>
                        <li>Spam job boards or employers through our platform</li>
                        <li>Violate the terms of service of third-party platforms (Indeed, LinkedIn, etc.)</li>
                        <li>Use the service to distribute malware or harmful code</li>
                        <li>Bypass rate limits or security measures</li>
                        <li>Share your account credentials with others</li>
                    </ul>

                    <div className="warning-box">
                        <strong>Important:</strong> Violation of this Acceptable Use Policy may result in immediate account termination without refund.
                    </div>
                </section>

                <section className="terms-section">
                    <h2>6. User Content and Data</h2>

                    <h3>6.1 Your Content</h3>
                    <p>
                        You retain ownership of all content you submit to the service, including your profile information, resumes, cover letters, and other materials. By using our service, you grant us a limited license to use, process, and store your content solely for the purpose of providing the service.
                    </p>

                    <h3>6.2 Content Accuracy</h3>
                    <p>You represent and warrant that:</p>
                    <ul>
                        <li>All information you provide is accurate and truthful</li>
                        <li>You have the right to share all content you submit</li>
                        <li>Your content does not violate any third-party rights</li>
                        <li>Your content does not contain confidential information belonging to others</li>
                    </ul>

                    <h3>6.3 AI Processing</h3>
                    <p>
                        Your content may be processed by AI services used by Launchway features. Third-party provider terms and privacy policies also apply.
                    </p>
                </section>

                <section className="terms-section">
                    <h2>7. Third-Party Services and Integrations</h2>

                    <h3>7.1 Google OAuth Integration</h3>
                    <p>
                        Our service requires access to your Google account to read and modify documents in Google Drive. By connecting your Google account, you grant us permission to access your Google Docs and Drive according to the scopes requested. You can revoke this access at any time through your Google account settings or our profile settings.
                    </p>

                    <h3>7.2 Job Boards and Platforms</h3>
                    <p>
                        When using Launchway with third-party job boards, ATS systems, and websites, you must comply with those platforms&apos; terms.
                    </p>

                    <h3>7.3 Third-Party Liability</h3>
                    <p>
                        We are not responsible for the actions, content, or policies of third-party services. Your use of third-party services is at your own risk. We do not endorse or guarantee the availability or quality of any third-party service.
                    </p>
                </section>

                <section className="terms-section">
                    <h2>8. Intellectual Property Rights</h2>

                    <h3>8.1 Our Property</h3>
                    <p>
                        All intellectual property rights in the Launchway platform, including software, design, logos, trademarks, and content, are owned by us or our licensors. You may not copy, modify, distribute, or create derivative works without our express written permission.
                    </p>

                    <h3>8.2 Trademarks</h3>
                    <p>
                        "Launchway" and our logo are trademarks of Launchway. You may not use our trademarks without prior written consent.
                    </p>

                    <h3>8.3 Feedback</h3>
                    <p>
                        If you provide feedback, suggestions, or ideas about our service, we may use them without any obligation to compensate you. You grant us a perpetual, irrevocable, worldwide license to use any feedback you provide.
                    </p>
                </section>

                <section className="terms-section">
                    <h2>9. Payment Terms and Credits</h2>

                    <h3>9.1 Service Credits</h3>
                    <p>
                        Launchway uses a credit/usage model for some features. Consumption can vary by feature and may change over time.
                    </p>

                    <h3>9.2 Credit Purchases</h3>
                    <p>
                        When you purchase credits, you agree to pay the stated price at the time of purchase. All credit purchases are final and non-refundable except as required by law.
                    </p>

                    <h3>9.3 Credit Expiration</h3>
                    <p>
                        Any active limits, balances, and usage counters are shown inside the product and can change with plan updates.
                    </p>

                    <h3>9.4 Pricing Changes</h3>
                    <p>
                        We reserve the right to change our pricing at any time. Price changes will not affect credits you have already purchased.
                    </p>
                </section>

                <section className="terms-section">
                    <h2>10. Privacy and Data Protection</h2>
                    <p>
                        Your use of Launchway data practices is governed by our <a href="/privacy-policy">Privacy Policy</a>, which is incorporated into these Terms.
                    </p>
                </section>

                <section className="terms-section">
                    <h2>11. Disclaimers and Limitations of Liability</h2>

                    <h3>11.1 Service "As Is"</h3>
                    <p>
                        THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT.
                    </p>

                    <h3>11.2 No Guarantee of Job Placement</h3>
                    <p>
                        We do not guarantee that using our service will result in job interviews or employment. Success in job applications depends on many factors beyond our control, including your qualifications, the job market, and employer preferences.
                    </p>

                    <h3>11.3 AI Accuracy</h3>
                    <p>
                        AI output can be wrong, incomplete, or not suitable for a specific context. You are responsible for reviewing and approving content and submissions.
                    </p>

                    <h3>11.4 Third-Party Platform Issues</h3>
                    <p>
                        We are not responsible for issues arising from third-party platforms, including but not limited to failed applications, account suspensions, or data breaches on job boards or other integrated services.
                    </p>

                    <h3>11.5 Limitation of Liability</h3>
                    <p>
                        TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
                    </p>
                    <ul>
                        <li>Your use or inability to use the service</li>
                        <li>Any unauthorized access to or use of your data</li>
                        <li>Any errors or omissions in the service</li>
                        <li>Any interruption or cessation of the service</li>
                        <li>Any bugs, viruses, or other harmful code transmitted through the service</li>
                    </ul>
                    <p>
                        OUR TOTAL LIABILITY TO YOU FOR ANY CLAIM ARISING FROM OR RELATED TO THESE TERMS OR THE SERVICE SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
                    </p>
                </section>

                <section className="terms-section">
                    <h2>12. Indemnification</h2>
                    <p>
                        You agree to indemnify, defend, and hold harmless Launchway, its officers, directors, employees, and agents from any claims, liabilities, damages, losses, or expenses (including legal fees) arising from:
                    </p>
                    <ul>
                        <li>Your violation of these Terms</li>
                        <li>Your violation of any third-party rights</li>
                        <li>Your misuse of the service</li>
                        <li>Any content you submit to the service</li>
                        <li>Your violation of applicable laws</li>
                    </ul>
                </section>

                <section className="terms-section">
                    <h2>13. Dispute Resolution and Governing Law</h2>

                    <h3>13.1 Governing Law</h3>
                    <p>
                        These Terms are governed by applicable law in the jurisdiction where the service operator is legally established.
                    </p>

                    <h3>13.2 Dispute Resolution</h3>
                    <p>
                        In the event of a dispute, you agree to first attempt to resolve the matter informally by contacting us at launchway@tailor-mycareer.online. We will attempt to resolve disputes in good faith within 30 days.
                    </p>

                    <h3>13.3 Arbitration</h3>
                    <p>
                        If informal resolution fails, dispute handling will follow applicable law and any required legal process.
                    </p>

                    <h3>13.4 Class Action Waiver</h3>
                    <p>
                        Where allowed by law, disputes should be handled on an individual basis.
                    </p>
                </section>

                <section className="terms-section">
                    <h2>14. Changes to Terms</h2>
                    <p>
                        We reserve the right to modify these Terms at any time. We will notify you of material changes by:
                    </p>
                    <ul>
                        <li>Updating the "Last Updated" date at the top of this page</li>
                        <li>Sending an email notification to your registered email address</li>
                        <li>Displaying a prominent notice on our platform</li>
                    </ul>
                    <p>
                        Your continued use of the service after changes become effective constitutes acceptance of the revised Terms. If you do not agree to the new Terms, you must stop using the service and may delete your account.
                    </p>
                </section>

                <section className="terms-section">
                    <h2>15. Termination</h2>

                    <h3>15.1 Termination by You</h3>
                    <p>
                        You may terminate your account at any time by using the account deletion feature in your profile settings. Upon termination:
                    </p>
                    <ul>
                        <li>All your data will be permanently deleted</li>
                        <li>Any unused credits will be forfeited</li>
                        <li>You will no longer have access to the service</li>
                    </ul>

                    <h3>15.2 Termination by Us</h3>
                    <p>
                        We may suspend or terminate your account immediately, without prior notice, if:
                    </p>
                    <ul>
                        <li>You violate these Terms</li>
                        <li>We are required to do so by law</li>
                        <li>Your account has been inactive for an extended period</li>
                        <li>We decide to discontinue the service</li>
                    </ul>

                    <h3>15.3 Effect of Termination</h3>
                    <p>
                        Upon termination, all licenses and rights granted to you will immediately cease. Provisions that by their nature should survive termination (including disclaimers, limitations of liability, and dispute resolution) will continue to apply.
                    </p>
                </section>

                <section className="terms-section">
                    <h2>16. Miscellaneous</h2>

                    <h3>16.1 Entire Agreement</h3>
                    <p>
                        These Terms, together with our Privacy Policy, constitute the entire agreement between you and Launchway regarding the service.
                    </p>

                    <h3>16.2 Severability</h3>
                    <p>
                        If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions will continue in full force and effect.
                    </p>

                    <h3>16.3 Waiver</h3>
                    <p>
                        Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.
                    </p>

                    <h3>16.4 Assignment</h3>
                    <p>
                        You may not assign or transfer these Terms or your account without our prior written consent. We may assign our rights and obligations under these Terms without restriction.
                    </p>

                    <h3>16.5 Force Majeure</h3>
                    <p>
                        We will not be liable for any failure or delay in performance due to circumstances beyond our reasonable control, including acts of God, natural disasters, war, terrorism, riots, or internet service failures.
                    </p>
                </section>

                <section className="terms-section">
                    <h2>17. Contact Information</h2>
                    <p>If you have questions or concerns about these Terms, please contact us:</p>
                    <div className="contact-info">
                        <p><strong>Email:</strong> launchway@tailor-mycareer.online</p>
                        <p><strong>Response Time:</strong> We aim to respond within 48 hours</p>
                    </div>
                    <p>
                        For account-related issues, you can also use the in-app support features or email us directly.
                    </p>
                </section>

                <div className="terms-footer">
                    <p>These Terms apply to users of Launchway web and CLI services.</p>
                    <p><strong>By using our service, you acknowledge that you have read, understood, and agree to be bound by these Terms.</strong></p>
                </div>
            </div>
        </div>
    );
};

export default TermsOfService;
